import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import {SearchRounded, RestartAltRounded} from "@mui/icons-material";
import { Tabs, Typography, Divider, Col, Row, Spin } from 'antd';
import { comparisonActions } from '../../Store/slices/comparison.slice';
import TickerSearch from '../../Components/TickerSearch/TickerSearch';
import StockChart from "../../charts/StockChart";
import PerformanceHistory from './Views/PerformanceHistory';
import Risk from './Views/Risk';
import PageHeader from "../../Components/PageHeader";
import { iconMapping } from "../../Configs/AppsConfig";
import {
    prepareOverviewChartData,
    prepareHistoricalPerformance,
    prepareCalendarReturns,
    prepareCalendarReturnsForSwarm,
} from './utils';
import ItemController from "./components/ItemController";
import PortfolioSelector from "../Portfolio/PortfolioSelector";
import NivoBarChart from "../../charts/nivo/NivoBarChart";
import NivoSwarmChart from "../../charts/nivo/NivoSwarmChart";
import PeakToValley from "./components/PeakToValley";
import HistoricalVolatility from "./Views/HistoricalVolatility";
import ReturnsDistributions from "./Views/ReturnsDistributions";
import * as PropTypes from "prop-types";
import Placeholder from "../../Components/Placeholder";
import _ from 'lodash';
const { TabPane } = Tabs;
const { Title, Text } = Typography;



const formatRequestPayload = (key, items, commonStartDate)=>{
  const formattedItems = items.map(item => ({
        id: item[key],
        label: item.name,
        startDate: commonStartDate
    }));
  return {items: formattedItems, key, commonStartDate}
}


/**
 *
 * @param initialObjects {Array[Object[{id, label}]], optional} - An array of objects containing id and label of the initial
 *                                                                ...timeseries portfolios/securities to load
 * @param isChildComponent {boolean, optional} - A boolean to indicate if the component is a child component. If
 * true, the component will not display the PageHeader and will not show the search bar and will not have padding
 * @param extraItems {Array[Object[{id, label}]], optional} - An array of objects containing id and label of the initial
 * @returns {JSX.Element}
 * @constructor
 */
const PerformanceComparison=({initialObjects, isChildComponent, extraItems})=>{
  const dispatch = useDispatch();
  const [showSearch, setShowSearch] = useState(initialObjects === null);
  const [selectedExtraItems, setSelectedExtraItems] = useState([]);
  const {items, commonStartDate, loading, itemColors} = useSelector(state => state.comparison);

  const previousInitialObjects = useRef(null);


  const fetchTimeSeriesData = useCallback(({ symbols, benchmark = undefined, labels }) => {
    if (!loading) {
      setShowSearch(false);
      const fullSymbolPayload = [...symbols];
      if (benchmark){
        fullSymbolPayload.push(...Array.isArray(benchmark) ? benchmark : [benchmark]);
      }
      dispatch(comparisonActions.fetchTimeSeriesData({
        symbols: fullSymbolPayload,
        startDate: commonStartDate,
        labels: labels
      }));
    }
  }, [dispatch, loading, commonStartDate]);

  // Use a ref to store the initialObjects to compare against new props

  console.log("ITEMS", items);

  useEffect(() => {
    if (!_.isEqual(previousInitialObjects.current, initialObjects)) {
      previousInitialObjects.current = initialObjects;
      if (initialObjects) {
        const symbols = initialObjects.map(item => item.id);
        const labels = initialObjects.map(item => ({ id: item.id, label: item.label }));
        fetchTimeSeriesData({ symbols, labels });
      }
    }
  }, [initialObjects, fetchTimeSeriesData]); // Only initialObjects and fetchTimeSeriesData in dependencies

  const handleSubmit = useCallback((itemsToSubmit) => {
      const newItems = itemsToSubmit.hasOwnProperty('items') ? itemsToSubmit.items : itemsToSubmit;
      fetchTimeSeriesData({symbols: newItems.map(item => item.id)});
      }, [fetchTimeSeriesData]);


  const handleTickersSubmit = useCallback((selectedTickers) => {
    const symbols = selectedTickers.map(x => x.symbol);
    const labels = selectedTickers.map(x => ({ id: x.symbol, label: x.name })); // Ensure labels are provided if needed
    fetchTimeSeriesData({symbols, labels});
    }, [handleSubmit, commonStartDate]);

  const handlePortfolioSubmit = useCallback((selectedPortfolios) => {
      const ids = selectedPortfolios.map(x=>x.id);
      const labels = selectedPortfolios.map(x => ({ id: x.id, label: x.name }));
      fetchTimeSeriesData({symbols: ids, labels});
  }, [handleSubmit, commonStartDate]);

  const handleRemoveItem = useCallback((id) => {
    dispatch(comparisonActions.removeItem({id}))
  }, [dispatch]);

  const toggleExtraItems = useCallback((ids) => {
    setSelectedExtraItems([...selectedExtraItems, ...ids]);
    fetchTimeSeriesData({symbols: ids});
  }, [extraItems, fetchTimeSeriesData, handleRemoveItem]);


  const reset = useCallback(() => {
    dispatch(comparisonActions.resetComparison());
    [[setShowSearch, true], [setSelectedExtraItems, []]].forEach(([setter, value]) => setter(value));
  }, [dispatch]);

  const mapColorsToLabels = useCallback(() => {
      const newMapping = {};
      items.forEach(item => {
        newMapping[item.label] = itemColors?.[item.id] || '#000000';
        if (item.id !== item.label){
            newMapping[item.id] = itemColors?.[item.id] || '#000000';
        }
      });
      return newMapping
    }, [items, itemColors]);


  if (loading){
      return (
          <Spin><Placeholder text={'Loading Comparison'}/></Spin>
      )
  }

  return (
        <div>
          {!isChildComponent && <PageHeader title={'Performance Comparison'}
                                            logoUrl={iconMapping.performanceComparison}
                                            actions={[
                                                <Button onClick={() => setShowSearch(true)} color={'primary'} variant={'contained'} startIcon={<SearchRounded/>}>
                                                  Search Tickers
                                                </Button>,
                                                <Button onClick={reset} color={'error'} variant={'outlined'} startIcon={<RestartAltRounded/>}>
                                                  Reset
                                                </Button>,
                                            ]}
                                />
          }
          {showSearch && !isChildComponent && (
              <Tabs defaultActiveKey={'tickers'} centered type={'card'} rootClassName={'py-3'}>
                <TabPane tab={'Search Tickers'} key={'tickers'}>
                  <TickerSearch
                      onTickersSubmit={handleTickersSubmit}
                      closeSearch={() => setShowSearch(false)}
                      maxTickers={3}
                  />
                </TabPane>
                <TabPane tab={'Select Portfolios'} key={'portfolios'}>
                  <PortfolioSelector onSelect={handlePortfolioSubmit} />
                </TabPane>
              </Tabs>
          )}
          {(
              <div className={!isChildComponent ? 'content-container' : ''}>
                {items.length > 0 && (
                    <div>
                      <ItemController
                          items={items}
                          itemColors={itemColors}
                          onRemove={handleRemoveItem}
                          extraItems={extraItems}
                          onToggleExtraItems={toggleExtraItems}
                          initialSelectedItems={selectedExtraItems}
                      />
                      <Divider/>
                      <Tabs defaultActiveKey="1" type={'card'}>
                        <TabPane tab="Overview" key="1">
                          <StockChart dataSeries={prepareOverviewChartData(items, itemColors)} allowChartInteractions={false} />
                          <Text style={{fontWeight: '600'}}>
                            Illustrative Performance is Based on Initial Investment of $10,000
                          </Text>
                        </TabPane>
                        <TabPane tab="Hypothetical Performance" key="2">
                          <div>
                              <PerformanceHistory data={prepareHistoricalPerformance([...items])} itemColors={mapColorsToLabels()}/>
                              <Divider/>
                              <div style={{height: 400}}>
                                  <NivoBarChart {...prepareCalendarReturns([...items], itemColors)} legendSize={'small'}/>
                              </div>
                              <Divider/>
                              <div style={{height: 500}}>
                                  <NivoSwarmChart {...prepareCalendarReturnsForSwarm(items, itemColors)}
                                              customColors={mapColorsToLabels()}
                                              yTitle={'Percentage Returns'}
                                              xTitle={'Year'}
                                  />
                              </div>
                          </div>
                        </TabPane>
                        <TabPane tab="Risk" key="3">
                          <Risk items={items} itemColors={mapColorsToLabels()}/>
                        </TabPane>
                        <TabPane tab={'Historical Volatility'} key={'6'}>
                            <HistoricalVolatility items={items} itemColors={itemColors}/>
                        </TabPane>
                        <TabPane tab={'Returns Distributions'} key={'7'}>
                            <ReturnsDistributions items={items} itemColors={itemColors}/>
                        </TabPane>
                      </Tabs>
                    </div>
                )}
              </div>
          )}
        </div>
    );

}


PerformanceComparison.propTypes = {
  initialObjects: PropTypes.any,
  isChildComponent: PropTypes.bool,
  extraItems: PropTypes.any
}

PerformanceComparison.defaultProps = {initialObjects: null, isChildComponent: false, extraItems: null}

export default PerformanceComparison;
